import { FormEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import FormGroup from '~/components/Form/FormGroup';
import PlainTextField from '~/components/Form/PlainTextField';
import Alert from '~/components/General/Alert';
import Button from '~/components/General/Button';
import { useMutateLogin } from '~/hooks/myEnvironment/useLogin';
import { useOptions } from '~/hooks/useOptions';
import useTranslation from '~/hooks/useTranslation';
import myEnvironmentRoutes from '~/routes/myEnvironment';
import authStyles from '~/styles/myEnvironment/auth.module.css';
import utilStyles from '~/styles/utils.module.css';
import { InferredMyEnvironmentOptions } from '~/types/options';
import IconArrowRight from '~icons/tabler/arrow-right';

export default function Login() {
  const options = useOptions() as InferredMyEnvironmentOptions;
  const navigate = useNavigate();
  const mutateLogin = useMutateLogin();
  const translate = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    packageId: '',
  });

  const onSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.email) {
      return;
    }

    try {
      await mutateLogin.mutateAsync({
        email: formData.email,
        package_id: formData.packageId,
        locale: options.locale,
        office_id: options.officeId,
        department_id: options.departmentId,
      });

      navigate(myEnvironmentRoutes.RESERVATIONS);
    } catch (error) {
      // Errors, validation errors are handled by the mutation object.
      // So we don't have to handle them here.
    }
  }, [
    formData,
    mutateLogin,
    navigate,
    options.locale,
    options.officeId,
    options.departmentId,
  ]);

  return (
    <div className={authStyles.authContent}>
      {import.meta.env.VITE_ENABLE_DATA_AVAILABILITY_WARNING === 'true' && (
        <div className={utilStyles.warningNote}>
          <div>
            The data on this environment is not kept up to date. We can only connect one
            environment to TravelSpirit. And that environment is the production environment.
          </div>
          <div>
            This environment requires manual data updates.
          </div>
        </div>
      )}

      <h1 className={authStyles.authTitle}>
        {translate('myEnvironment.login.title')}
      </h1>

      <Alert
        type="danger"
        error={mutateLogin.error}
      />

      <form onSubmit={onSubmit}>
        <FormGroup>
          <PlainTextField
            label={translate('form.email')}
            fieldType="email"
            value={formData.email}
            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
            required
          />
        </FormGroup>

        <FormGroup>
          <PlainTextField
            label={translate('form.package_id')}
            fieldType="text"
            value={formData.packageId}
            onChange={(event) => setFormData({ ...formData, packageId: event.target.value })}
            required
          />
        </FormGroup>

        <Button
          type="submit"
          disabled={mutateLogin.isPending}
        >
          <span>{translate('myEnvironment.login.login')}</span>
          <IconArrowRight />
        </Button>
      </form>
    </div>
  );
}
